// @ts-nocheck

import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { type KonvaNodeComponent } from 'react-konva';
import styled, { css } from 'styled-components';

import Client, { Target } from '../../../client';
import MobileModal from '../../../components/MobileModal';
import Modal from '../../../components/Modal';
import { ModalFormat } from '../../../constants/ui/modal';
import { getPortalRootDOMElement } from '../../../DOMElements';
import { __ } from '../../../i18n/l10n';
import { callNative, requestTypes } from '../../../lib/bridge';
import colors from '../../../styled-components/palette';
import { webShare } from '../../../utils';
import { useIsFontLoaded, useLoadImages } from '../hooks';

import Canvas from './Canvas';

const HiddenCanvas = styled.div`
  display: none;
`;

const ImgWrapper = styled.div`
  display: flex;
  margin: 0.6em;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Img = styled.img`
  &&&& {
    width: auto;
    height: auto;
    max-width: 75%;
    border: solid 1px ${colors.bluegrey800};
    border-radius: 1em;

    ${Target.__MOBILE__
      ? css`
          max-width: 100%;
          margin-top: 2em;
        `
      : null}
  }
`;

const Preview = styled.div``;

const InfoWrapper = styled.div`
  color: #000;
  background: #ececec;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5em 3em;
  text-align: center;
  font-size: 1em;
  line-height: 1.2;
  font-family: BettingPoliticaExtraBold, sans-serif;
  margin-top: 1.3em;
`;

const betshareFonts = {
  BettingPoliticaItalic: {},
  BettingPoliticaExtraBold: {},
  BettingPoliticaExtraBoldItalic: {},
};

type BetshareProps = {
  outcomesDetails: Array<unknown>;
  betslipReference: string;
  assets: unknown;
  canvasAreas: unknown;
  needAskBetslipInfo: boolean;
  onClose: () => void;
  askBetslipInfo: () => void;
  nickname?: string | null;
  showPseudo: boolean;
  showStack: boolean;
};

const Betshare = ({
  outcomesDetails,
  betslipReference,
  assets,
  canvasAreas,
  needAskBetslipInfo,
  askBetslipInfo,
  onClose,
  nickname,
  showPseudo = false,
  showStack = false,
}: BetshareProps) => {
  const refStage = useRef<KonvaNodeComponent>(null);

  const [imageUrl, setImageUrl] = useState(null);
  const [isReadyToShare, setIsReadyToShare] = useState(false);
  const [shouldRetry] = useState(false);

  /**
   * Usage of hooks to check asynchronously if assets (fonts/images) are loaded
   * @param {object}    assetToCheck     The object of assets or fonts we need to check before drawing
   * @param {boolean}   shouldRetry      A boolean passed allowing to retry when user clicks after getting an error
   *
   * @return {object} Returns an object containing a boolean (isAssetLoaded), an error object containing errorType and errorText
   * and for the images, returns the loaded images
   */
  const fontLoadingResponse = useIsFontLoaded(betshareFonts, shouldRetry);
  const isFontLoaded = fontLoadingResponse.isFontLoaded;

  const imagesLoadingResponse = useLoadImages(assets, shouldRetry);
  const isImagesLoaded = imagesLoadingResponse.isImagesLoaded;
  const isImagesLoadedError = imagesLoadingResponse.isImagesLoadedError;
  const images = useMemo(
    () => (!isImagesLoadedError ? imagesLoadingResponse.images : {}),
    [isImagesLoadedError, imagesLoadingResponse],
  );

  const isCanvasMode = useMemo(() => imageUrl === null, [imageUrl]);

  // The display is based on this condition. We are ready to draw when all data, assets and fonts are loaded correctly
  const isReadyToDraw = useMemo(
    () => outcomesDetails.length > 0 && Object.keys(images).length > 0 && isImagesLoaded && isFontLoaded,
    [outcomesDetails.length, images, isImagesLoaded, isFontLoaded],
  );

  useEffect(() => {
    if (needAskBetslipInfo) {
      askBetslipInfo();
    }
  }, [askBetslipInfo, betslipReference, needAskBetslipInfo]);

  useEffect(() => {
    if (isReadyToShare) {
      (async () => {
        // we get pixelRatio used by konva, its device pixelRatio but we limit to 4 to avoid assets to be stretched
        const pixelRatio = Math.min(refStage.current.getLayers()[0].getCanvas().pixelRatio, 4);
        const dataURL = refStage.current.toDataURL({
          pixelRatio,
          mimeType: 'image/jpeg',
        });

        try {
          if (Client.embed) {
            onClose();
            await callNative(requestTypes.SHARE_BASE64_IMAGE, dataURL);
          } else if (navigator.share && Target.__MOBILE__) {
            onClose();
            await webShare.shareFile(dataURL, 'betshare.jpg');
          } else {
            throw new Error();
          }
        } catch (err) {
          // Fallback
          if (Target.__ENV__ !== 'production') {
            console.error(err);
          }
          setImageUrl(dataURL);
        }
      })();
    }
  }, [isReadyToShare, onClose, refStage, setImageUrl]);

  const renderShareableImage = useCallback(
    () => (
      <Preview data-testid="betshare-preview">
        <ImgWrapper>
          <Img src={imageUrl} alt={`betshare-${betslipReference}`} />
        </ImgWrapper>
        <InfoWrapper>
          {Target.__MOBILE__
            ? __('Click and hold an image to save or share')
            : __('Right-click an image to copy it or to save it on your device')}
        </InfoWrapper>
      </Preview>
    ),
    [betslipReference, imageUrl],
  );

  if (isReadyToDraw) {
    return (
      <>
        <HiddenCanvas>
          <Canvas
            ref={refStage}
            betslipReference={betslipReference}
            outcomesDetails={outcomesDetails}
            images={images}
            showPseudo={showPseudo}
            nickname={nickname}
            showWinningsAndStake={showStack}
            setIsReadyToShare={setIsReadyToShare}
            isReadyToShare={isReadyToShare}
            canvasAreas={canvasAreas}
          />
        </HiddenCanvas>

        {Target.__MOBILE__ ? (
          <MobileModal isOpen={!isCanvasMode} onClose={onClose} fullHeight>
            {renderShareableImage()}
          </MobileModal>
        ) : null}

        {!isCanvasMode && !Target.__MOBILE__
          ? createPortal(
              <Modal
                onClose={onClose}
                forbidBackgroundScrolling
                hasOverlay
                closeOnEsc
                closeOnOutsideClick
                hasCloseButton
                isCloseButtonInside={Target.__MOBILE__}
                hideOverlay
                noOverflowHidden
                blockHeight
                format={Target.__MOBILE__ ? ModalFormat.FULLSCREEN : ModalFormat.DESKTOP}
              >
                {renderShareableImage()}
              </Modal>,
              getPortalRootDOMElement(),
            )
          : null}
      </>
    );
  }

  return null;
};

export default Betshare;
